import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SubscriptionService from "../../services/subscription";
import decodeToken from "../../utils/decodeToken";
import { toast } from "react-toastify";
import NoImg from "../../assets/no-image.png";
import "./MySubscriptions.css";

const MySubscriptions = () => {
  const navigate = useNavigate();
  const { user_id } = decodeToken();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (user_id) {
      fetchSubscriptions();
    } else {
      setLoading(false);
    }
  }, [user_id]);

  const fetchSubscriptions = async () => {
    try {
      const response =
        await SubscriptionService.fetchMentorSubscriptions(user_id);
      if (response.data.status) {
        setSubscriptions(response.data.subscriptions || []);
      }
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
      toast.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (subscriptionId, kidName, cancelAtEnd = false) => {
    const message = cancelAtEnd
      ? `Cancel sponsorship for ${kidName} at the end of current billing cycle?`
      : `Cancel sponsorship for ${kidName} immediately?`;

    if (!window.confirm(message)) {
      return;
    }

    setActionLoading(subscriptionId);
    try {
      await SubscriptionService.cancelSubscription(subscriptionId, cancelAtEnd);
      toast.success("Subscription cancelled successfully");
      fetchSubscriptions();
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (subscriptionId) => {
    setActionLoading(subscriptionId);
    try {
      await SubscriptionService.pauseSubscription(subscriptionId);
      toast.success("Subscription paused successfully");
      fetchSubscriptions();
    } catch (error) {
      toast.error("Failed to pause subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (subscriptionId) => {
    setActionLoading(subscriptionId);
    try {
      await SubscriptionService.resumeSubscription(subscriptionId);
      toast.success("Subscription resumed successfully");
      fetchSubscriptions();
    } catch (error) {
      toast.error("Failed to resume subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: "Active", color: "#4CAF50", bg: "#E8F5E9" },
      authenticated: {
        label: "Pending Payment",
        color: "#FF9800",
        bg: "#FFF3E0",
      },
      created: { label: "Created", color: "#2196F3", bg: "#E3F2FD" },
      paused: { label: "Paused", color: "#FF9800", bg: "#FFF3E0" },
      cancelled: { label: "Cancelled", color: "#f44336", bg: "#FFEBEE" },
      completed: { label: "Completed", color: "#9C27B0", bg: "#F3E5F5" },
      halted: { label: "Payment Failed", color: "#f44336", bg: "#FFEBEE" },
      expired: { label: "Expired", color: "#757575", bg: "#EEEEEE" },
      pending: { label: "Pending", color: "#FF9800", bg: "#FFF3E0" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className="statusBadge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (!user_id) {
    return (
      <div className="mySubscriptionsContainer">
        <div className="noAuth">
          <h2>Please Login</h2>
          <p>You need to be logged in to view your subscriptions.</p>
          <button onClick={() => navigate("/login")} className="loginBtn">
            Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mySubscriptionsContainer">
        <div className="loadingState">
          <div className="spinner"></div>
          <p>Loading your subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mySubscriptionsContainer">
      <div className="pageHeader">
        <h1>My Sponsorships</h1>
        <p>Manage your recurring sponsorship subscriptions</p>
      </div>

      {subscriptions.length === 0 ? (
        <div className="emptyState">
          <div className="emptyIcon">📋</div>
          <h2>No Active Subscriptions</h2>
          <p>You don't have any sponsorship subscriptions yet.</p>
          <button onClick={() => navigate("/kids")} className="browseKidsBtn">
            Browse Kids to Sponsor
          </button>
        </div>
      ) : (
        <div className="subscriptionsList">
          {subscriptions.map((sub) => (
            <div key={sub.id} className="subscriptionCard">
              <div className="cardHeader">
                <div className="kidInfo">
                  <img
                    src={sub.kid_avatar || NoImg}
                    alt={sub.kid_name}
                    className="kidAvatar"
                  />
                  <div className="kidDetails">
                    <h3>{sub.kid_name}</h3>
                    <span className="categoryName">
                      {sub.category_name || "Education"}
                    </span>
                  </div>
                </div>
                {getStatusBadge(sub.status)}
              </div>

              <div className="cardBody">
                <div className="infoGrid">
                  <div className="infoItem">
                    <span className="label">Monthly Amount</span>
                    <span className="value">
                      ₹{sub.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="infoItem">
                    <span className="label">Payments Made</span>
                    <span className="value">
                      {sub.paid_count || 0} / {sub.total_count}
                    </span>
                  </div>
                  <div className="infoItem">
                    <span className="label">Next Charge</span>
                    <span className="value">{formatDate(sub.charge_at)}</span>
                  </div>
                  <div className="infoItem">
                    <span className="label">Started On</span>
                    <span className="value">{formatDate(sub.created_at)}</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="progressContainer">
                  <div className="progressLabel">
                    <span>Progress</span>
                    <span>
                      {Math.round((sub.paid_count / sub.total_count) * 100)}%
                    </span>
                  </div>
                  <div className="progressBar">
                    <div
                      className="progressFill"
                      style={{
                        width: `${(sub.paid_count / sub.total_count) * 100}%`,
                        backgroundColor:
                          sub.status === "active" ? "#4CAF50" : "#FF9800",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="cardActions">
                {sub.status === "active" && (
                  <>
                    <button
                      onClick={() => handlePause(sub.id)}
                      disabled={actionLoading === sub.id}
                      className="actionBtn pauseBtn"
                    >
                      {actionLoading === sub.id ? "..." : "Pause"}
                    </button>
                    <button
                      onClick={() => handleCancel(sub.id, sub.kid_name, true)}
                      disabled={actionLoading === sub.id}
                      className="actionBtn cancelBtn"
                    >
                      {actionLoading === sub.id ? "..." : "Cancel"}
                    </button>
                  </>
                )}

                {sub.status === "paused" && (
                  <>
                    <button
                      onClick={() => handleResume(sub.id)}
                      disabled={actionLoading === sub.id}
                      className="actionBtn resumeBtn"
                    >
                      {actionLoading === sub.id ? "..." : "Resume"}
                    </button>
                    <button
                      onClick={() => handleCancel(sub.id, sub.kid_name, false)}
                      disabled={actionLoading === sub.id}
                      className="actionBtn cancelBtn"
                    >
                      {actionLoading === sub.id ? "..." : "Cancel"}
                    </button>
                  </>
                )}

                {(sub.status === "created" ||
                  sub.status === "authenticated" ||
                  sub.status === "pending") && (
                  <a
                    href={sub.short_url || "#"}
                    className="actionBtn completePaymentBtn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Complete Payment
                  </a>
                )}

                {(sub.status === "cancelled" || sub.status === "completed") && (
                  <button
                    onClick={() => navigate(`/checkout/${sub.kid_id}`)}
                    className="actionBtn renewBtn"
                  >
                    Sponsor Again
                  </button>
                )}

                <button
                  onClick={() => navigate(`/subscription/${sub.id}`)}
                  className="actionBtn viewBtn"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MySubscriptions;
